// Function index:
//   MountRoomWorldDeps  — inputs needed to mount a room-graph interior.
//   mountRoomWorld(deps) — mount the room scene, attach move handlers, and export a hex-compatible adapter.

import { mountRoomScene } from '../room-scene.js';
import type { WS } from '../ws.js';
import type { BootstrapResponse } from '../api.js';
import type { SceneMount } from '../hex-scene/types.js';

export interface MountRoomWorldDeps {
  appEl: HTMLElement;
  boot: BootstrapResponse;
  ws: WS;
  isPaused: () => boolean;
  selfCharId: number | null;
  charNames: Map<number, string>;
}

export interface RoomWorldMount {
  scene: SceneMount;
  destroy(): void;
}

export function mountRoomWorld(deps: MountRoomWorldDeps): RoomWorldMount {
  const { appEl, boot, ws, isPaused, selfCharId } = deps;
  const scene = mountRoomScene(appEl, {
    rooms: boot.rooms,
    doors: boot.doors,
    npcs: boot.npcs.map((n) => ({ id: n.id, name: n.name, archetype: n.archetype, q: n.q, r: n.r })),
    characters: boot.party_characters.map((c) => ({
      id: c.id,
      name: c.name,
      q: c.current_q,
      r: c.current_r,
      isSelf: c.id === selfCharId,
    })),
    selfQ: boot.character?.current_q ?? 0,
    selfR: boot.character?.current_r ?? 0,
  });

  scene.onRoomClick((q, r) => {
    if (isPaused()) return;
    ws.send('player.move.intent', { q, r });
  });

  const roomCharacterMovedHandler = (msg: { character_id: number; q: number; r: number }): void => {
    if (msg.character_id !== selfCharId) return;
    scene.setSelfPosition(msg.q, msg.r);
  };

  ws.on('character.moved', roomCharacterMovedHandler);

  const adapter: SceneMount = {
    setTerrain: () => undefined,
    discover: () => undefined,
    addCharacter: (c) => scene.addCharacter({
      id: c.id,
      name: c.name,
      q: c.q,
      r: c.r,
      isSelf: c.isSelf,
    }),
    removeCharacter: (id) => scene.removeCharacter(id),
    moveCharacter: (id, q, r) => scene.moveCharacter(id, q, r),
    setSelfPosition: (q, r) => scene.setSelfPosition(q, r),
    addNpc: () => undefined,
    removeNpc: () => undefined,
    moveNpc: (id, q, r) => scene.moveNpc(id, q, r),
    onHexClick: () => undefined,
    onHexRightClick: () => undefined,
    addHexes: () => undefined,
    onNeedHexes: () => undefined,
    destroy: () => undefined,
  };

  return {
    scene: adapter,
    destroy: () => {
      ws.off('character.moved', roomCharacterMovedHandler);
      scene.destroy();
    },
  };
}
